import React, { useState, useMemo } from "react";
import { Table } from "reactstrap";
import { Image } from "../../../../../AbstractElements";
import { dynamicImage } from "../../../../../Utils";

export interface HeadDataItem {
  id: string;
  head: string | JSX.Element;
  key: string;
  sortKey?: string | null;
}

interface CommonTableNewProps {
  tableClass?: string;
  strip?: boolean;
  caption?: string;
  size?: string;
  hover?: boolean;
  headClass?: string;
  headRowClass?: string;
  headData: HeadDataItem[];
  data: any[];
  renderRow: (row: any, index: number) => React.ReactNode;
  initialRows?: number; // New prop to control the number of rows shown initially
}

const CommonTableNew: React.FC<CommonTableNewProps> = ({
  tableClass,
  strip,
  caption,
  size,
  hover,
  headClass = "",
  headRowClass = "",
  headData,
  data,
  renderRow,
  initialRows = 10, // Default to 10 rows if not provided
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);

  const handleSort = (key: string) => {
    setSortConfig((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  const getByPath = (obj: any, path?: string) => {
    if (!path) return undefined;
    if (!path.includes(".")) return obj?.[path];
    return path
      .split(".")
      .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  };

  const sortedData = useMemo(() => {
    let sorted = [...data];
    if (sortConfig?.key) {
      sorted.sort((a, b) => {
        const aVal = getByPath(a, sortConfig.key);
        const bVal = getByPath(b, sortConfig.key);
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <Table className={tableClass} striped={strip} hover={hover} size={size}>
          {caption && <caption>{caption}</caption>}
          <thead className={headClass}>
            <tr className={headRowClass}>
              {headData.map((head) => {
                const isSortable = !!head.sortKey;
                const isActive = sortConfig?.key === head.sortKey;
                const direction = sortConfig?.direction;

                return (
                  <th
                    key={head.id}
                    style={{
                      cursor: isSortable ? "pointer" : "default",
                      padding: "4px 8px",
                      position: "relative",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      color: "inherit",
                    }}
                    onClick={() => isSortable && handleSort(head.sortKey!)}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {head.head}
                      {isSortable && (
                        <span
                          style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            fontSize: "10px",
                            lineHeight: "10px",
                            marginLeft: "2px",
                          }}
                        >
                          <span
                            style={{
                              color:
                                isActive && direction === "asc"
                                  ? "#1c65d3"
                                  : "#ccc",
                              fontWeight:
                                isActive && direction === "asc"
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            ▲
                          </span>
                          <span
                            style={{
                              color:
                                isActive && direction === "desc"
                                  ? "#1c65d3"
                                  : "#ccc",
                              fontWeight:
                                isActive && direction === "desc"
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            ▼
                          </span>
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, index) => renderRow(row, index))}
            {currentRows.length === 0 && (
              <tr>
                <td colSpan={headData.length} className="text-center">
                  <div style={{ padding: 20 }}>
                    <img
                      src={dynamicImage(`user/noimagenew.png`)}
                      alt="no-data"
                      style={{
                        maxWidth: 110,
                        width: "100%",
                        height: "auto",
                        opacity: 1,
                      }}
                    />
                    <div style={{ marginTop: 8, color: "#666" }}>
                      No data found.
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {sortedData.length > initialRows && (
        <div className="d-flex justify-content-between align-items-center px-2 pb-2 flex-wrap gap-2">
          <div className="p-1">
            <label className="me-2">Rows per page:</label>
            <select
              className="form-select d-inline-block w-auto"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              style={{
                padding: "2px 10px",
                paddingRight: "20px",
                fontSize: "14px",
              }}
            >
              {[10, 25, 50, 100, 500].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="p-1">
            <label>
              Showing {startIndex + 1}–
              {Math.min(startIndex + rowsPerPage, sortedData.length)} of{" "}
              {sortedData.length} records
            </label>
          </div>

          <div className="p-1">
            <label>
              Page {currentPage} of {totalPages}
            </label>
            <button
              disabled={currentPage === 1}
              className="btn btn-sm btn-outline-primary ms-2"
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <button
              disabled={currentPage === totalPages}
              className="btn btn-sm btn-outline-primary ms-2"
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CommonTableNew;
